import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useCustomer } from "../context/CustomerContext";
import { redirectAfterCustomerAuth } from "../utils/customerAuthRedirect";
import GoogleCustomerAuthButton from "./GoogleCustomerAuthButton";
import styles from "./RegisterCustomerModal.module.css";

const RegisterCustomerModal = ({ onClose, onLoginClick }) => {
  const navigate = useNavigate();
  const { loginWithGoogle } = useCustomer();
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phone: "+380",
    dateOfBirth: "",
    email: "",
    password: "",
    passwordConfirm: "",
  });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Стани для зайнятості
  const [emailExists, setEmailExists] = useState(false);
  const [phoneExists, setPhoneExists] = useState(false);

  // Перевірка унікальності з debounce
  useEffect(() => {
    const timeout = setTimeout(async () => {
      if (form.email || form.phone) {
        try {
          const res = await axios.get("/api/customers/check-unique", {
            params: { email: form.email, phone: form.phone },
          });
          setEmailExists(res.data.emailExists);
          setPhoneExists(res.data.phoneExists);
        } catch {
          // ігноруємо помилки локалки
        }
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [form.email, form.phone]);

  const validate = () => {
    let newErrors = {};
    if (!form.firstName) newErrors.firstName = true;
    if (!form.lastName) newErrors.lastName = true;
    if (!/^\+380\d{9}$/.test(form.phone)) newErrors.phone = true;
    if (!form.dateOfBirth) newErrors.dateOfBirth = true;
    if (!form.email) newErrors.email = true;
    if (!form.password) newErrors.password = true;
    if (!form.passwordConfirm) newErrors.passwordConfirm = true;
    if (form.password !== form.passwordConfirm)
      newErrors.passwordConfirm = "Паролі не співпадають";
    if (emailExists) newErrors.email = "Цей email вже зайнятий";
    if (phoneExists) newErrors.phone = "Цей телефон вже зайнятий";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!validate()) {
      setError("Заповніть всі поля коректно");
      return;
    }

    const dob = new Date(form.dateOfBirth);
    const now = new Date();
    const age =
      now.getFullYear() -
      dob.getFullYear() -
      (now < new Date(now.getFullYear(), dob.getMonth(), dob.getDate())
        ? 1
        : 0);
    if (age < 14) {
      setError("Вам має бути не менше 14 років");
      return;
    }

    setLoading(true);
    try {
      await axios.post("/api/customers/register", {
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone,
        dateOfBirth: form.dateOfBirth,
        email: form.email,
        password: form.password,
      });
      setSuccessMessage(
        "Реєстрацію успішно завершено! Тепер Ви можете увійти у свій кабінет."
      );
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Помилка під час реєстрації, спробуйте ще раз."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = async (credential) => {
    setError("");
    setLoading(true);

    try {
      const data = await loginWithGoogle(credential);
      if (onClose) onClose();
      redirectAfterCustomerAuth(navigate, {
        isNewCustomer: Boolean(data?.isNewCustomer),
      });
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Не вдалося зареєструватися через Google. Спробуйте ще раз."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = (message) => {
    setError(message || "Google авторизацію скасовано або не завершено.");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Якщо змінюється телефон — формат + перевірка
    if (name === "phone") {
      let val = value.replace(/\D/g, "");
      if (!val.startsWith("380")) val = "380";
      val = "+" + val.slice(0, 12);
      setForm((f) => ({ ...f, phone: val }));

      // При зміні телефону знімаємо помилку та стан "зайнятий"
      setErrors((prev) => ({ ...prev, phone: undefined }));
      setPhoneExists(false);
      return;
    }

    // Зміна email
    if (name === "email") {
      setForm((f) => ({ ...f, email: value }));
      setErrors((prev) => ({ ...prev, email: undefined }));
      setEmailExists(false);
      return;
    }

    // Інші поля
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));

    // Якщо це паролі — скидаємо помилку підтвердження
    if (name === "password" || name === "passwordConfirm") {
      setErrors((prev) => ({ ...prev, passwordConfirm: undefined }));
    }
  };

  const modalContent = (
    <div className={styles.backdrop}>
      <div className={styles.modalWindow}>
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Закрити"
        >
          ×
        </button>
        {successMessage ? (
          <div className={styles.successMessage}>
            <p>{successMessage}</p>
            <button
              className={`${styles.submit} ${styles.wideButton}`}
              onClick={() => {
                localStorage.setItem("redirectAfterFirstLogin", "true");
                onLoginClick();
              }}
              style={{ marginTop: 20 }}
            >
              Увійти
            </button>
          </div>
        ) : (
          <>
            <h3 className={styles.formTitle}>Реєстрація</h3>
            <form
              className={styles.form}
              onSubmit={handleSubmit}
              autoComplete="off"
            >
              {/* Ім'я */}
              <label className={styles.label}>
                Ім’я
                <input
                  className={`${styles.input} ${
                    errors.firstName ? styles.inputError : ""
                  }`}
                  name="firstName"
                  type="text"
                  value={form.firstName}
                  onChange={handleChange}
                  disabled={loading}
                />
              </label>

              {/* Прізвище */}
              <label className={styles.label}>
                Прізвище
                <input
                  className={`${styles.input} ${
                    errors.lastName ? styles.inputError : ""
                  }`}
                  name="lastName"
                  type="text"
                  value={form.lastName}
                  onChange={handleChange}
                  disabled={loading}
                />
              </label>

              {/* Телефон */}
              <label className={styles.label}>
                Телефон
                <input
                  className={`${styles.input} ${
                    errors.phone || phoneExists ? styles.inputError : ""
                  }`}
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  maxLength={13}
                  disabled={loading}
                />
                {phoneExists && (
                  <div className={styles.error}>
                    Користувач з таким номером телефона вже зареєстрований
                  </div>
                )}
              </label>

              {/* Дата народження */}
              <label className={styles.label}>
                Дата народження
                <input
                  className={`${styles.input} ${
                    errors.dateOfBirth ? styles.inputError : ""
                  }`}
                  name="dateOfBirth"
                  type="date"
                  value={form.dateOfBirth}
                  onChange={handleChange}
                  max={new Date().toISOString().split("T")[0]}
                  disabled={loading}
                />
              </label>

              {/* Email */}
              <label className={styles.label}>
                Email
                <input
                  className={`${styles.input} ${
                    !!errors.email || emailExists ? styles.inputError : ""
                  }`}
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  disabled={loading}
                />
                {emailExists && (
                  <div className={styles.error}>
                    Користувач з такою електронною поштою вже зареєстрований
                  </div>
                )}
              </label>

              {/* Пароль */}
              <div className={styles.field}>
                <label>Пароль</label>
                <div className={styles.inputPasswordWrapper}>
                  <input
                    className={`${styles.input} ${
                      errors.password ? styles.inputError : ""
                    }`}
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className={styles.eyeButton}
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? <FiEye /> : <FiEyeOff />}
                  </button>
                </div>
              </div>

              {/* Підтвердження паролю */}
              <div className={styles.field}>
                <label>Підтвердження паролю</label>
                <div className={styles.inputPasswordWrapper}>
                  <input
                    className={`${styles.input} ${
                      errors.passwordConfirm ? styles.inputError : ""
                    }`}
                    name="passwordConfirm"
                    type={showPasswordConfirm ? "text" : "password"}
                    value={form.passwordConfirm}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className={styles.eyeButton}
                    onClick={() => setShowPasswordConfirm((v) => !v)}
                  >
                    {showPasswordConfirm ? <FiEye /> : <FiEyeOff />}
                  </button>
                </div>
                {errors.passwordConfirm && errors.passwordConfirm !== true && (
                  <div className={styles.error}>{errors.passwordConfirm}</div>
                )}
              </div>

              {error && <div className={styles.error}>{error}</div>}
              <button
                className={styles.submit}
                type="submit"
                disabled={loading || emailExists || phoneExists}
              >
                {loading ? "Реєстрація..." : "Зареєструватися"}
              </button>
            </form>
            <GoogleCustomerAuthButton
              label="Зареєструватися через Google"
              disabled={loading}
              onCredential={handleGoogleCredential}
              onError={handleGoogleError}
            />

            <div className={styles.switchLine}>
              Вже є акаунт?
              <span className={styles.link} onClick={onLoginClick}>
                Увійти
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(modalContent, document.body);
};

export default RegisterCustomerModal;
